<script setup lang="ts">
import { nextTick, ref } from 'vue'
import { useAuthStore, type TricksterTargetUser } from '@/stores/useAuthStore'
import { useUserAdminStore, type UserAdminItem } from '@/stores/useUserAdminStore'

const authStore = useAuthStore()
const adminStore = useUserAdminStore()

const loading = ref(false)

// -- Switch d'utilisateur ----------------------------------------------------
// L'impersonation ne porte pas les droits USER_MASTER : pour chercher un user
// et ré-impersonner, on restaure d'abord l'admin (sans navigation), puis on
// relance un "en tant que" et on recharge la page courante. Une annulation
// ré-impersonne le user d'origine pour revenir à l'état initial.
const switching = ref(false)
const originalUser = ref<TricksterTargetUser | null>(null)
const search = ref('')
const results = ref<UserAdminItem[]>([])
const searchLoading = ref(false)
const searchInput = ref<HTMLInputElement | null>(null)
let searchTimer: ReturnType<typeof setTimeout> | null = null

async function stopTrickster() {
  loading.value = true
  await authStore.stopTrickster()
  window.location.href = '/admin/users'
}

async function openSwitch() {
  if (!authStore.tricksterUser) return
  originalUser.value = { ...authStore.tricksterUser }
  loading.value = true
  // Restaure l'admin → on récupère les droits pour rechercher / ré-impersonner.
  const ok = await authStore.stopTrickster()
  loading.value = false
  if (!ok) {
    // Restauration impossible : on garde l'impersonation en cours.
    authStore.syncTricksterFromMe(originalUser.value)
    return
  }
  switching.value = true
  search.value = ''
  results.value = []
  await nextTick()
  searchInput.value?.focus()
}

function onSearchInput() {
  if (searchTimer) clearTimeout(searchTimer)
  searchTimer = setTimeout(runSearch, 300)
}

async function runSearch() {
  const q = search.value.trim()
  if (q.length < 2) {
    results.value = []
    return
  }
  searchLoading.value = true
  try {
    const { data } = await adminStore.fetchUsers({ search: q, limit: 10, status: 'ACTIVE' })
    results.value = data
  } finally {
    searchLoading.value = false
  }
}

function userLabel(u: { firstName: string | null; lastName: string | null; email: string }): string {
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim()
  return name ? `${name} (${u.email})` : u.email
}

async function switchTo(user: UserAdminItem) {
  loading.value = true
  const result = await adminStore.generateTricksterToken(user.uuid)
  if (result) {
    authStore.startTrickster(result)
    window.location.reload()
  } else {
    loading.value = false
  }
}

async function cancelSwitch() {
  // On était admin pendant la recherche : on ré-impersonne le user d'origine
  // pour restaurer l'état initial (la page n'a pas été rechargée).
  if (originalUser.value) {
    loading.value = true
    const result = await adminStore.generateTricksterToken(originalUser.value.uuid)
    if (result) authStore.startTrickster(result)
    loading.value = false
  }
  switching.value = false
  results.value = []
  search.value = ''
}
</script>

<template>
  <div
    v-if="authStore.tricksterUser || switching"
    class="sticky top-0 left-0 right-0 z-9999 flex items-center justify-center gap-3 bg-warning text-warning-content px-4 py-2 text-sm font-medium shadow-lg"
  >
    <!-- Mode normal : impersonation active -->
    <template v-if="!switching">
      <span>
        Connecte en tant que
        <strong>{{ authStore.tricksterUser?.firstName }} {{ authStore.tricksterUser?.lastName }}</strong>
        ({{ authStore.tricksterUser?.email }})
      </span>
      <button
        class="btn btn-sm btn-ghost border border-warning-content/30"
        :disabled="loading"
        @click="openSwitch"
      >
        {{ loading ? '...' : "Changer d'utilisateur" }}
      </button>
      <button
        class="btn btn-sm btn-ghost border border-warning-content/30"
        :disabled="loading"
        @click="stopTrickster"
      >
        {{ loading ? 'Restauration...' : 'Revenir a mon identite' }}
      </button>
    </template>

    <!-- Mode switch : recherche d'un autre utilisateur -->
    <template v-else>
      <span class="whitespace-nowrap">Connexion en tant que :</span>
      <div class="relative w-96 max-w-[60vw]">
        <input
          ref="searchInput"
          v-model="search"
          type="text"
          class="input input-sm input-bordered w-full text-base-content"
          placeholder="Rechercher un utilisateur (nom ou email)..."
          :disabled="loading"
          @input="onSearchInput"
        />
        <div
          v-if="search.trim().length >= 2"
          class="absolute left-0 right-0 top-full mt-1 max-h-72 overflow-y-auto rounded-md bg-base-100 text-base-content shadow-xl ring-1 ring-black/10"
        >
          <div v-if="searchLoading" class="px-3 py-2 text-sm opacity-70">Recherche...</div>
          <ul v-else-if="results.length > 0" class="menu menu-sm">
            <li v-for="u in results" :key="u.uuid">
              <button type="button" :disabled="loading" @click="switchTo(u)">
                {{ userLabel(u) }}
              </button>
            </li>
          </ul>
          <div v-else class="px-3 py-2 text-sm opacity-70">Aucun utilisateur trouvé</div>
        </div>
      </div>
      <button
        class="btn btn-sm btn-ghost border border-warning-content/30"
        :disabled="loading"
        @click="cancelSwitch"
      >
        Annuler
      </button>
    </template>
  </div>
</template>
